import type { Incident, IncidentReviewMediaRef } from '../../services/incidentsApi';

const DEMO5_CAMERA_IDS = new Set(['cam_a1bc71b70d77', 'cam_a6c12b0f3d94']);

function text(value: unknown): string {
  return String(value || '').trim();
}

export function incidentUsesReviewerAidPrimaryPolicy(
  incident: Incident | null | undefined,
): boolean {
  const deviceId = text(incident?.device_id).toLowerCase();
  const zoneId = text(incident?.zone_id).toLowerCase();
  const cameraId = text(incident?.camera_id);
  return deviceId === 'demo5' || zoneId === 'demo5tes' || DEMO5_CAMERA_IDS.has(cameraId);
}

export function incidentReviewerAidSnapshot(
  incident: Incident | null | undefined,
): IncidentReviewMediaRef | null {
  const reviewMedia = incident?.review_media;
  const primary = reviewMedia?.primary_snapshot || reviewMedia?.reviewer_aids?.snapshot || null;
  return text(primary?.filename) ? primary : null;
}

export function incidentSourceTruthSnapshot(
  incident: Incident | null | undefined,
): IncidentReviewMediaRef | null {
  const reviewMedia = incident?.review_media;
  const secondary = reviewMedia?.secondary_snapshot || reviewMedia?.source_truth?.snapshot || null;
  return text(secondary?.filename) ? secondary : null;
}

export function incidentSourceTruthClip(
  incident: Incident | null | undefined,
): IncidentReviewMediaRef | null {
  const clip = incident?.review_media?.source_truth?.clip || null;
  return text(clip?.filename) ? clip : null;
}

export function incidentDisplayedSnapshot(
  incident: Incident | null | undefined,
): IncidentReviewMediaRef | null {
  return incidentReviewerAidSnapshot(incident) || incidentSourceTruthSnapshot(incident);
}

export function isPreAnnotatedReviewerAidSnapshot(
  ref: IncidentReviewMediaRef | null | undefined,
): boolean {
  return (
    text(ref?.review_role).toLowerCase() === 'reviewer_aid' ||
    Boolean(text(ref?.snapshot_overlay))
  );
}

export function reviewerImageTypeLabel(
  incident: Incident | null | undefined,
): string | null {
  const reviewerAid = incidentReviewerAidSnapshot(incident);
  if (text(reviewerAid?.display_label)) return text(reviewerAid?.display_label);
  if (reviewerAid) return 'Reviewer aid: annotated';
  return null;
}

export function rawSourceEvidenceLabel(
  incident: Incident | null | undefined,
): string | null {
  const snapshot = incidentSourceTruthSnapshot(incident);
  if (text(snapshot?.display_label)) return text(snapshot?.display_label);
  if (snapshot || incidentSourceTruthClip(incident)) return 'Raw source evidence';
  return null;
}

export function reviewerImageBboxStatusLabel(
  incident: Incident | null | undefined,
): string | null {
  const displayed = incidentDisplayedSnapshot(incident);
  const reviewerAid = incidentReviewerAidSnapshot(incident);
  const explicit = text(displayed?.bbox_status_label);
  if (explicit) return explicit;
  if (reviewerAid && text(reviewerAid?.snapshot_overlay).toLowerCase() !== 'detections') {
    return 'BBox unavailable: no aligned detection frame';
  }
  return null;
}

export function reviewerImageIncidentSubjectLabel(
  incident: Incident | null | undefined,
): string | null {
  const displayed = incidentDisplayedSnapshot(incident);
  const explicit = text(displayed?.incident_subject_label);
  return explicit || null;
}

export function reviewerImageSubjectIdentityRetained(
  incident: Incident | null | undefined,
): boolean {
  const displayed = incidentDisplayedSnapshot(incident);
  if (displayed?.subject_identity_retained === true) return true;
  if (displayed?.subject_identity_retained === false) return false;
  return Boolean(text(displayed?.trigger_subject_key));
}

export function reviewerImageSubjectMetadataChipLabel(
  incident: Incident | null | undefined,
): string | null {
  const label = reviewerImageIncidentSubjectLabel(incident);
  if (!label) return null;
  if (reviewerImageSubjectIdentityRetained(incident)) {
    return `Incident subject: ${label}`;
  }
  return `Detected class: ${label}`;
}

export function reviewerImageTriggerVerificationLabel(
  incident: Incident | null | undefined,
): string | null {
  const displayed = incidentDisplayedSnapshot(incident);
  const explicit = text(displayed?.trigger_person_verification_label);
  return explicit || null;
}

export function reviewerImageTriggerVerificationStatus(
  incident: Incident | null | undefined,
): string | null {
  const displayed = incidentDisplayedSnapshot(incident);
  const explicit = text(displayed?.trigger_person_verification_status);
  return explicit || null;
}

function numericConfidence(raw: unknown): number | null {
  const value = Number(raw);
  if (!Number.isFinite(value)) return null;
  return value;
}

function confidenceFromDetections(detections: unknown): number | null {
  if (!Array.isArray(detections)) return null;
  let best: number | null = null;
  let fallback: number | null = null;
  for (const detection of detections) {
    if (!detection || typeof detection !== 'object') continue;
    const item = detection as Record<string, unknown>;
    const label = text(item.class || item.label || item.type || item.entity_type).toLowerCase();
    if (label && !['person', 'pedestrian', 'human'].includes(label)) continue;
    const candidates = [
      item.confidence,
      item.object_confidence,
      item.person_confidence,
      item.detection_confidence,
      item.association_confidence,
      item.score,
    ];
    const pe = item.person_evidence_v1;
    if (pe && typeof pe === 'object') {
      const personEvidence = pe as Record<string, unknown>;
      candidates.push(
        personEvidence.confidence,
        personEvidence.detection_confidence,
        personEvidence.association_confidence,
      );
    }
    let parsed: number | null = null;
    for (const raw of candidates) {
      parsed = numericConfidence(raw);
      if (parsed !== null) break;
    }
    if (parsed === null) continue;
    if (text(item.overlay_source) === 'incident_bbox') {
      fallback = fallback === null ? parsed : Math.max(fallback, parsed);
      continue;
    }
    best = best === null ? parsed : Math.max(best, parsed);
  }
  return best ?? fallback;
}

export function extractReviewMediaConfidence(
  ref: IncidentReviewMediaRef | null | undefined,
  metadata?: Record<string, unknown> | null,
): number | null {
  const fromRef = numericConfidence(ref?.confidence);
  if (fromRef !== null) return fromRef;
  const extra =
    metadata?.extra && typeof metadata.extra === 'object'
      ? (metadata.extra as Record<string, unknown>)
      : null;
  const sources = [
    metadata?.confidence,
    metadata?.object_confidence,
    metadata?.person_confidence,
    extra?.confidence,
    extra?.object_confidence,
    extra?.person_confidence,
  ];
  for (const raw of sources) {
    const parsed = numericConfidence(raw);
    if (parsed !== null) return parsed;
  }
  return confidenceFromDetections(metadata?.detections) ?? confidenceFromDetections(extra?.detections);
}

export function formatSnapshotConfidenceLabel(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  if (value >= 0 && value <= 1) return `Confidence: ${Math.round(value * 100)}%`;
  if (value > 1 && value <= 100) return `Confidence: ${Math.round(value)}%`;
  return `Confidence: ${value.toFixed(2)}`;
}

export function formatPersonConfidenceLabel(value: number | null | undefined): string {
  const base = formatSnapshotConfidenceLabel(value);
  if (!base) return '';
  return base.replace(/^Confidence:/, 'Person confidence:');
}

export function formatForkliftConfidenceLabel(value: number | null | undefined): string {
  const base = formatSnapshotConfidenceLabel(value);
  if (!base) return '';
  return base.replace(/^Confidence:/, 'Forklift confidence:');
}

function forkliftConfidenceFromDetections(detections: unknown): number | null {
  if (!Array.isArray(detections)) return null;
  let best: number | null = null;
  for (const detection of detections) {
    if (!detection || typeof detection !== 'object') continue;
    const item = detection as Record<string, unknown>;
    const label = text(item.class || item.label || item.type || item.entity_type).toLowerCase();
    if (!label.startsWith('forklift')) continue;
    for (const raw of [item.confidence, item.object_confidence, item.detection_confidence, item.score]) {
      const parsed = numericConfidence(raw);
      if (parsed === null) continue;
      best = best === null ? parsed : Math.max(best, parsed);
      break;
    }
  }
  return best;
}

export function extractIncidentForkliftConfidence(
  incident: Incident | null | undefined,
  metadata?: Record<string, unknown> | null,
): number | null {
  const metrics =
    incident?.metrics && typeof incident.metrics === 'object'
      ? (incident.metrics as Record<string, unknown>)
      : null;
  const attrs =
    metrics?.attributes && typeof metrics.attributes === 'object'
      ? (metrics.attributes as Record<string, unknown>)
      : null;
  const proximity =
    metrics?.proximity_evidence && typeof metrics.proximity_evidence === 'object'
      ? (metrics.proximity_evidence as Record<string, unknown>)
      : null;
  const extra =
    metadata?.extra && typeof metadata.extra === 'object'
      ? (metadata.extra as Record<string, unknown>)
      : null;
  const proximityMetrics =
    extra?.proximity_metrics && typeof extra.proximity_metrics === 'object'
      ? (extra.proximity_metrics as Record<string, unknown>)
      : null;
  for (const raw of [
    attrs?.forklift_confidence,
    proximity?.forklift_confidence,
    metrics?.forklift_confidence,
    proximityMetrics?.forklift_confidence,
    metadata?.forklift_confidence,
    extra?.forklift_confidence,
  ]) {
    const parsed = numericConfidence(raw);
    if (parsed !== null) return parsed;
  }
  const displayed = incidentDisplayedSnapshot(incident) as
    | (IncidentReviewMediaRef & { detections?: unknown })
    | null
    | undefined;
  return (
    forkliftConfidenceFromDetections(metadata?.detections) ??
    forkliftConfidenceFromDetections(extra?.detections) ??
    forkliftConfidenceFromDetections(displayed?.detections)
  );
}

export function forkliftSubjectMetadataChipLabel(
  incident: Incident | null | undefined,
): string | null {
  const actorsList = incident?.actors;
  const actors = Array.isArray(actorsList) ? actorsList : [];
  const hasForkliftActor = actors.some((actor: unknown) => {
    if (!actor || typeof actor !== 'object') return false;
    const row = actor as Record<string, unknown>;
    const entity = text(row.entity_type || row.class || row.label).toLowerCase();
    const track = text(row.forklift_track_id || row.track_id).toLowerCase();
    return entity.includes('forklift') || track.startsWith('forklift');
  });
  const metrics =
    incident?.metrics && typeof incident.metrics === 'object'
      ? (incident.metrics as Record<string, unknown>)
      : null;
  const attrs =
    metrics?.attributes && typeof metrics.attributes === 'object'
      ? (metrics.attributes as Record<string, unknown>)
      : null;
  const trackId = text(
    attrs?.forklift_track_id ||
      (attrs?.forklift_actor && typeof attrs.forklift_actor === 'object'
        ? (attrs.forklift_actor as Record<string, unknown>).track_id
        : null),
  );
  if (!hasForkliftActor && !trackId && extractIncidentForkliftConfidence(incident) === null) {
    return null;
  }
  return trackId ? `Detected class: Forklift (${trackId})` : 'Detected class: Forklift';
}

export type ReviewerImageDrawerContext = {
  strictReviewerImagePolicy: boolean;
  reviewerAidSnapshotRef: IncidentReviewMediaRef | null;
  sourceTruthSnapshotRef: IncidentReviewMediaRef | null;
  sourceTruthClipRef: IncidentReviewMediaRef | null;
  primaryReviewerImageLabel: string | null;
  secondaryEvidenceLabel: string | null;
  reviewerAidBboxStatusLabel: string | null;
  incidentSubjectLabel: string | null;
  incidentSubjectMetadataChipLabel: string | null;
  incidentSubjectConfidence: number | null;
  forkliftSubjectMetadataChipLabel: string | null;
  forkliftConfidence: number | null;
  triggerVerificationLabel: string | null;
  triggerVerificationStatus: string | null;
};

export function buildReviewerImageDrawerContext(
  incident: Incident | null | undefined,
): ReviewerImageDrawerContext {
  return {
    strictReviewerImagePolicy: incidentUsesReviewerAidPrimaryPolicy(incident),
    reviewerAidSnapshotRef: incidentReviewerAidSnapshot(incident),
    sourceTruthSnapshotRef: incidentSourceTruthSnapshot(incident),
    sourceTruthClipRef: incidentSourceTruthClip(incident),
    primaryReviewerImageLabel: reviewerImageTypeLabel(incident),
    secondaryEvidenceLabel: rawSourceEvidenceLabel(incident),
    reviewerAidBboxStatusLabel: reviewerImageBboxStatusLabel(incident),
    incidentSubjectLabel: reviewerImageIncidentSubjectLabel(incident),
    incidentSubjectMetadataChipLabel: reviewerImageSubjectMetadataChipLabel(incident),
    incidentSubjectConfidence: extractReviewMediaConfidence(
      incidentDisplayedSnapshot(incident),
      undefined,
    ),
    forkliftSubjectMetadataChipLabel: forkliftSubjectMetadataChipLabel(incident),
    forkliftConfidence: extractIncidentForkliftConfidence(incident),
    triggerVerificationLabel: reviewerImageTriggerVerificationLabel(incident),
    triggerVerificationStatus: reviewerImageTriggerVerificationStatus(incident),
  };
}
